import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  getGuideIndex,
  getGuideContent,
  type GuideIndex,
  type GuideContentEnhanced,
} from '../lib/api'

/**
 * Map route prefixes to guide section slugs.
 * Ordered by specificity (longest prefix first) so matching works correctly.
 */
const ROUTE_TO_GUIDE: [string, string][] = [
  ['/workspace', 'workspaces'],
  ['/apps', 'apps'],
  ['/settings', 'getting-started'],
  ['/changelog', 'getting-started'],
  ['/guide', 'getting-started'],
  ['/admin', 'getting-started'],
  ['/', 'getting-started'],
]

function resolveSection(pathname: string): string {
  for (const [prefix, section] of ROUTE_TO_GUIDE) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return section
    }
  }
  return 'getting-started'
}

export function useHelpDrawer() {
  const { pathname } = useLocation()

  const [isOpen, setIsOpen] = useState(false)
  const [guideIndex, setGuideIndex] = useState<GuideIndex | null>(null)
  const [indexLoading, setIndexLoading] = useState(false)
  const [currentSection, setCurrentSection] = useState('')
  const [sectionContent, setSectionContent] = useState<GuideContentEnhanced | null>(null)
  const [loading, setLoading] = useState(false)

  // Listen for programmatic drawer open events (from HelpLink component)
  useEffect(() => {
    const handler = (e: Event) => {
      const { section } = (e as CustomEvent).detail
      setCurrentSection(section)
      setIsOpen(true)
    }
    window.addEventListener('help:open', handler)
    return () => window.removeEventListener('help:open', handler)
  }, [])

  const openDrawer = useCallback(() => {
    const section = resolveSection(pathname)
    setCurrentSection(section)
    setIsOpen(true)
  }, [pathname])

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Fetch guide index on first open
  useEffect(() => {
    if (!isOpen || guideIndex) return

    let cancelled = false
    setIndexLoading(true)

    getGuideIndex()
      .then((data) => {
        if (!cancelled) {
          setGuideIndex(data)
        }
      })
      .catch(() => {
        // Silently fail - guide index is non-critical
      })
      .finally(() => {
        if (!cancelled) {
          setIndexLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, guideIndex])

  // Fetch section content when currentSection changes
  useEffect(() => {
    if (!currentSection) return

    let cancelled = false
    setLoading(true)
    setSectionContent(null)

    getGuideContent(currentSection)
      .then((data) => {
        if (!cancelled) {
          setSectionContent(data)
        }
      })
      .catch(() => {
        // Silently fail
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [currentSection])

  return {
    isOpen,
    openDrawer,
    closeDrawer,
    guideIndex,
    currentSection,
    setCurrentSection,
    sectionContent,
    loading,
    indexLoading,
  }
}
